import "@/App.css";
import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Documents from "@/pages/Documents";
import DocumentView from "@/pages/DocumentView";
import Archived from "@/pages/Archived";
import Correspondence from "@/pages/Correspondence";
import Companies from "@/pages/Companies";
import Alerts from "@/pages/Alerts";
import UsersPage from "@/pages/Users";
import Pending from "@/pages/Pending";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

function AuthCallback() {
  const { processSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    (async () => {
      if (!sessionId) { navigate("/login", { replace: true }); return; }
      try {
        window.history.replaceState(null, "", window.location.pathname);
        await processSession(sessionId);
        navigate("/", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Authentication failed";
        navigate("/login", { replace: true, state: { authError: msg } });
      }
    })();
  }, [location.hash, navigate, processSession]);

  return <Loader />;
}

function Protected({ children, adminOnly, superadminOnly }) {
  const { user, ready, isAdmin, isSuperadmin, isActive } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isActive) return <Pending />;
  if (superadminOnly && !isSuperadmin) return <Navigate to="/" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/documents" element={<Protected><Documents /></Protected>} />
      <Route path="/documents/:id" element={<Protected><DocumentView /></Protected>} />
      <Route path="/archived" element={<Protected><Archived /></Protected>} />
      <Route path="/correspondence" element={<Protected><Correspondence /></Protected>} />
      <Route path="/companies" element={<Protected><Companies /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/users" element={<Protected adminOnly><UsersPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
